<script setup lang="ts">
    import { __ } from '../../stores/expressions';
    import IconButton from '../IconButton.vue';
    import DateRangeFilter from './DateRangeFilter.vue';
    import CheckboxFilter from './CheckboxFilter.vue';
    import SearchableCheckboxFilter from './SearchableCheckboxFilter.vue';
    import { useFilterStoreByName } from '@/js/stores/filter';
    import { computed, watch } from 'vue';
    import AllFiltersItem from './AllFiltersItem.vue';
    import SentimentFilter from './SentimentFilter.vue';
    import TrackerFilter from './TrackerFilter.vue';

    import TimeRangeFilter from './TimeRangeFilter.vue';
    import { EchoFilterCheckbox, FilterStoreName } from '@/js/services/api/filter/types';
    import type { HideableFilter } from './types';
    import MultiSelectInput from '../MultiSelectInput.vue';
    import ResolvedIssuesFilter from './ResolvedIssuesFilter.vue';
    import LoadingDots from '../LoadingDots.vue';
    import EchoButton from '../EchoButton.vue';
    import RelativeDateRangeFilter from './RelativeDateRangeFilter.vue';
    import SvgIcon from '../SvgIcon.vue';
    import InvalidFiltersWarning from './InvalidFiltersWarning.vue';
    import {
        ArrowRightIcon,
        BrainIcon,
        CheckIcon,
        ClipboardCheckIcon,
        ClockIcon,
        DatabaseIcon,
        EyeIcon,
        HeadphonesIcon,
        LayersIcon,
        ListFilterIcon,
        ShapesIcon,
        TriangleAlertIcon,
        XIcon,
    } from '@lucide/vue';

    const props = withDefaults(
        defineProps<{
            alwaysVisible?: boolean;
            isVisible: boolean;
            filterStoreName: FilterStoreName;
            hiddenFilters?: HideableFilter[];
            matchTablePadding?: boolean;
            // parentComponent: string; <!-- Search filter hidden for now -->
        }>(),
        { matchTablePadding: false }
    );

    const emit = defineEmits<{
        (e: 'toggleVisibility', value: boolean): void;
    }>();

    const filter = useFilterStoreByName(props.filterStoreName);

    function dateChanged(value: { start: string; end: string }) {
        filter.setStartEndTime(value.start, value.end);
    }

    const is_visible = computed(() => {
        return props.isVisible || props.alwaysVisible;
    });

    const hiddenSet = computed(() => new Set(props.hiddenFilters ?? []));
    const isHidden = (key: HideableFilter) => hiddenSet.value.has(key);

    // The active filters row shows no chip for an invalid level filter, so open the panel to show its warning.
    watch(
        () => !!filter.all?.levelFilterMatchesNothing,
        (matchesNothing) => {
            if (!matchesNothing || is_visible.value || isHidden('level')) return;
            emit('toggleVisibility', true);
        },
        { immediate: true }
    );

    // Checkbox is "Include unanalyzed": checked means hasTranscriptions=false.
    const includeUnanalyzed = computed({
        get: () => filter.all?.hasTranscriptions === false,
        set: (value: boolean) => {
            if (filter.all) filter.all.hasTranscriptions = !value;
        },
    });
</script>

<template>
    <aside
        class="All-filters pt-5"
        :class="{
            'All-filters--visible border-l border-l-gray-lightest': is_visible,
        }"
        :aria-label="__('all-filters')"
    >
        <div class="All-filters__header">
            <h2 class="sr-only">
                {{ __('all-filters') }}
            </h2>

            <div
                v-if="is_visible && (!filter.all || filter.loading)"
                class="absolute bottom-5.25 right-14 z-10"
            >
                <LoadingDots :color="false" />
            </div>

            <div
                class="top-5.5"
                :class="
                    is_visible
                        ? 'hidden relative lg:flex lg:left-4'
                        : `absolute -translate-x-full ${props.matchTablePadding ? '-left-7.5' : '-left-5'}`
                "
            >
                <EchoButton
                    :aria-expanded="is_visible"
                    aria-controls="all-filters-list"
                    variant="tertiary"
                    @click="$emit('toggleVisibility', !isVisible)"
                >
                    {{ __('all-filters') }}
                    <template #icon-right><ListFilterIcon class="size-5" /></template>
                </EchoButton>
            </div>

            <IconButton
                v-if="isVisible && !alwaysVisible"
                :aria-label="__('close')"
                size="size-6"
                area-size="size-10"
                class="right-0.5 top-5.5 absolute"
                @click="$emit('toggleVisibility', false)"
            >
                <XIcon class="size-5" />
                <span class="sr-only">{{ __('hide-filters') }}</span>
            </IconButton>
        </div>

        <div
            v-show="is_visible"
            v-if="filter.all"
            id="all-filters-list"
            class="All-filters__list custom-scrollbar"
        >
            <InvalidFiltersWarning
                v-if="filter.all.levelFilterMatchesNothing && !isHidden('level')"
                :categories="[__('filter-level')]"
                @remove="filter.removeInvalidLevelFilter()"
            />

            <AllFiltersItem
                v-if="
                    filter.all.protocolFormDefinitionFilter && !isHidden('protocolFormDefinition')
                "
            >
                <template #icon><ClipboardCheckIcon class="size-5" /></template>
                {{ __('forms') }}
                <template #content>
                    <CheckboxFilter :checkboxes="filter.all.protocolFormDefinitionFilter" />
                </template>
            </AllFiltersItem>

            <AllFiltersItem v-if="!isHidden('resolvedIssues')">
                <template #icon><CheckIcon class="size-5" /></template>
                {{ __('interactions-solved') }}
                <template #content>
                    <ResolvedIssuesFilter :filter-store-name="props.filterStoreName" />
                </template>
            </AllFiltersItem>

            <AllFiltersItem v-if="filter.all.skillFilter && !isHidden('skill')">
                <template #icon><BrainIcon class="size-5" /></template>
                {{ __('filter-skills') }}
                <template #content>
                    <CheckboxFilter :checkboxes="filter.all.skillFilter" />
                </template>
            </AllFiltersItem>

            <AllFiltersItem v-if="filter.all.queueFilter && !isHidden('queue')">
                <template #icon><BrainIcon class="size-5" /></template>
                {{ __('filter-queues') }}
                <template #content>
                    <CheckboxFilter :checkboxes="filter.all.queueFilter" />
                </template>
            </AllFiltersItem>

            <AllFiltersItem
                v-if="filter.all.levelFilter && !isHidden('level')"
                :open="filter.levelFilterWasReset"
            >
                <template #icon><LayersIcon class="size-5" /></template>
                {{ __('filter-level') }}
                <template #content>
                    <p
                        v-if="filter.levelFilterWasReset"
                        role="status"
                        class="flex items-start gap-1.5 px-4 pb-2 text-caption text-gray-dark"
                    >
                        <TriangleAlertIcon
                            class="size-4 shrink-0 text-sentiment-negative mt-0.5"
                            aria-hidden="true"
                        />
                        <span>
                            <strong>{{ __('filter-level') }}</strong>
                            {{ __('filter-category-was-reset') }}
                        </span>
                    </p>
                    <CheckboxFilter :checkboxes="filter.all.levelFilter" />
                </template>
            </AllFiltersItem>

            <RelativeDateRangeFilter
                v-if="
                    props.filterStoreName === FilterStoreName.WidgetFilter &&
                    !isHidden('relativeDateRange')
                "
                :value="filter.all.relativeDateRange"
                @update:value="(v) => filter.setRelativeDateRange(v)"
            />

            <DateRangeFilter
                v-if="!isHidden('dateRange')"
                :start="filter.all.startTime"
                :end="filter.all.endTime"
                @change="dateChanged($event)"
            />

            <AllFiltersItem v-if="filter.all.trackerFilter?.filter && !isHidden('tracker')">
                <template #icon><EyeIcon class="size-5" /></template>
                {{ __('filter-trackers') }}
                <template #content>
                    <TrackerFilter :filter-store-name="props.filterStoreName" />
                </template>
            </AllFiltersItem>

            <AllFiltersItem v-if="filter.all.channelFilter && !isHidden('channel')">
                <template #icon><HeadphonesIcon class="size-5" /></template>
                {{ __('filter-channel') }}
                <template #content>
                    <CheckboxFilter
                        :checkboxes="filter.all.channelFilter"
                        expression-base="channel-name-"
                    />
                </template>
            </AllFiltersItem>

            <AllFiltersItem v-if="filter.all.categoryFilter && !isHidden('category')">
                <template #icon><ShapesIcon class="size-5" /></template>
                {{ __('filter-category') }}
                <template #content>
                    <CheckboxFilter :checkboxes="filter.all.categoryFilter" />
                </template>
            </AllFiltersItem>

            <SentimentFilter
                v-if="filter.all.sentimentAgentFilter && !isHidden('sentimentAgent')"
                :checkboxes="filter.all.sentimentAgentFilter"
                type="sentiment"
            >
                <template #icon>
                    <SvgIcon name="sentiment-excited.svg" size="size-5" />
                </template>
                {{ __('filter-sentiment-agent') }}
            </SentimentFilter>

            <SentimentFilter
                v-if="
                    filter.all.sentimentAgentDirectionFilter && !isHidden('sentimentAgentDirection')
                "
                :checkboxes="filter.all.sentimentAgentDirectionFilter"
                type="direction"
            >
                <template #icon><ArrowRightIcon class="size-5" /></template>
                {{ __('filter-sentiment-agent-direction') }}
            </SentimentFilter>

            <SentimentFilter
                v-if="filter.all.sentimentCustomerFilter && !isHidden('sentimentCustomer')"
                :checkboxes="filter.all.sentimentCustomerFilter"
                type="sentiment"
            >
                <template #icon>
                    <SvgIcon name="sentiment-excited.svg" size="size-5" />
                </template>
                {{ __('filter-sentiment-customer') }}
            </SentimentFilter>

            <SentimentFilter
                v-if="
                    filter.all.sentimentCustomerDirectionFilter &&
                    !isHidden('sentimentCustomerDirection')
                "
                :checkboxes="filter.all.sentimentCustomerDirectionFilter"
                type="direction"
            >
                <template #icon><ArrowRightIcon class="size-5" /></template>
                {{ __('filter-sentiment-customer-direction') }}
            </SentimentFilter>

            <AllFiltersItem v-if="!isHidden('timeRange')">
                <template #icon><ClockIcon class="size-5" /></template>
                {{ __('filter-length-audio-file') }}

                <template #content>
                    <TimeRangeFilter :filter-store-name="props.filterStoreName" />
                </template>
            </AllFiltersItem>

            <AllFiltersItem>
                <template #icon><BrainIcon class="size-5" /></template>
                {{ __('filter-analysis') }}

                <template #content>
                    <ul class="px-4 pb-5">
                        <li class="relative">
                            <label class="flex items-start py-1 text-body">
                                <input
                                    v-model="includeUnanalyzed"
                                    type="checkbox"
                                    class="Custom-checkbox"
                                />
                                {{ __('include-unanalyzed') }}
                            </label>
                        </li>
                    </ul>
                </template>
            </AllFiltersItem>

            <!-- Search filter hidden for now -->
            <!-- <KeywordFilter
                v-if="parentComponent === 'interactionsfilter' && filter.all.keywordFilter"
                icon="search.svg"
                :filter-store-name="props.filterStoreName"
                >{{ __('filter-keyword') }}</KeywordFilter
            > -->

            <template
                v-for="customFilter in isHidden('customData') ? [] : filter.all.customDataFilter"
                :key="customFilter.key"
            >
                <AllFiltersItem>
                    <template #icon><DatabaseIcon class="size-5" /></template>
                    {{ customFilter.name || __('custom-data-filter') }}

                    <template #content>
                        <CheckboxFilter
                            v-if="customFilter.filterType === 'Checkbox'"
                            :checkboxes="customFilter.filter as EchoFilterCheckbox[]"
                        />
                        <SearchableCheckboxFilter
                            v-if="customFilter.filterType === 'SearchableCheckbox'"
                            v-model:filter="customFilter.filter"
                            :custom-data-type-id="customFilter.key"
                        />
                        <div v-if="customFilter.filterType === 'Text'" class="px-4 pb-6">
                            <label
                                :for="'customFilterText_' + customFilter.key"
                                class="pt-1 mb-0.5 block"
                            >
                                {{ __('search') }}
                            </label>
                            <MultiSelectInput
                                :id="'customFilterText_' + customFilter.key"
                                :items="customFilter.words || []"
                                @update:items="customFilter.words = $event"
                            />
                        </div>
                    </template>
                </AllFiltersItem>
            </template>
        </div>
    </aside>
</template>
